/**
 * Market evidence field — V5-only interactive graphic for the Market Signal.
 *
 * The default state is a clean 2025-only horizontal chart: four category
 * rails drawn against the shared max-relative scale (18,525). Compare 2019
 * mounts one dominant full-width annual comparison stage — a neutral 2019
 * baseline rail measured as 17,170 / 23,938, an amber extension to the 2025
 * endpoint, and a full-width 2025 rail — above four signed category rails
 * that expose every exact pair, signed percent and signed count at once.
 *
 * The annual stage uses its own scale (2025 = 100%); the category rails use
 * the shared 18,525 maximum. The two surfaces are separate blocks because
 * the four categories do not reconcile to the annual total.
 *
 * 2019 is the last full pre-pandemic year. Its figures are the revised
 * values in the Insolvency Service December 2025 long-run CSV:
 * https://assets.publishing.service.gov.uk/media/696e1539bbcea094189e2383/Long-Run_Series_in_CSV_Format_-_Company_Insolvency_Statistics_December_2025.csv
 *
 * All readable content is HTML; the rails are decorative aria-hidden CSS.
 * There is no SVG text, logo, magnifier, pie, donut, canvas or image, and
 * no 2019 value exists in the readable DOM until the toggle is pressed.
 * Motion is a finite mount sequence (anchor -> measure -> resolve, ends by
 * 840ms) driven purely by DOM state and CSS keyframes — no JS timers.
 *
 * Structure contract: `.market-evidence-field` carries only the toggle,
 * key, annual stage and live readout. The category rows are a sibling so
 * that excising the figure leaves exactly the V1 breakdown copy — the
 * default rows expose the V1 label and 2025-value strings and nothing
 * else. Comparison state lives on the `.market-evidence-frame` wrapper.
 */

import { useState } from 'react';
import { CountUp } from '@/components/roi/CountUp';
import { getMarketEvidenceRatio } from './market-evidence';

export interface MarketEvidenceDatum {
  label: string;
  /** 2025 value. */
  value: number;
  /** Revised 2019 value from the December 2025 long-run series. */
  previousValue: number;
}

type ChangeDirection = 'increase' | 'decrease' | 'flat';

const PREVIOUS_YEAR = 2019;
const CURRENT_YEAR = 2025;

function formatCount(value: number): string {
  return value.toLocaleString('en-GB');
}

/** Signed change, always derived from the displayed values. */
function formatDelta(delta: number): string {
  return `${delta >= 0 ? '+' : '-'}${formatCount(Math.abs(delta))}`;
}

/** Rounded percentage change, always derived from the displayed values. */
function formatPercentChange(previous: number, current: number): string {
  if (
    !Number.isFinite(previous) ||
    !Number.isFinite(current) ||
    previous === 0
  ) {
    return '—';
  }

  const percentage = Math.round(
    ((current - previous) / Math.abs(previous)) * 100,
  );
  return `${percentage > 0 ? '+' : ''}${percentage}%`;
}

function getDirection(previous: number, current: number): ChangeDirection {
  if (current > previous) return 'increase';
  if (current < previous) return 'decrease';
  return 'flat';
}

function toPercent(ratio: number): string {
  return `${(ratio * 100).toFixed(3)}%`;
}

export function MarketEvidenceField({
  data,
  comparisonData,
  activeIndex,
  onActivateIndex,
  totalPrevious,
  totalCurrent,
  reducedMotion = false,
  comparisonMode = 'toggle',
  showAnnualComparison = true,
}: {
  /** Complete 2025 overview shown before comparison is enabled. */
  data: MarketEvidenceDatum[];
  /** Optional focused subset mounted only while comparison is enabled. */
  comparisonData?: MarketEvidenceDatum[];
  activeIndex: number;
  /** Row selection callback for click, focus and hover synchronization. */
  onActivateIndex?: (index: number) => void;
  /** Exact published 2019 annual total. Denominates only the annual stage. */
  totalPrevious: number;
  /** Exact published 2025 annual total. Denominates only the annual stage. */
  totalCurrent: number;
  reducedMotion?: boolean;
  /** Keep the release-to-release comparison visible without a toggle. */
  comparisonMode?: 'toggle' | 'always';
  /** The focused V1 comparison retains the headline total outside this chart. */
  showAnnualComparison?: boolean;
}) {
  const [toggleComparing, setToggleComparing] = useState(false);
  const comparing = comparisonMode === 'always' || toggleComparing;
  const displayedData = comparing && comparisonData ? comparisonData : data;
  const displayedActiveIndex = Math.min(
    activeIndex,
    Math.max(0, displayedData.length - 1),
  );
  const activeDatum = displayedData[displayedActiveIndex];

  /** Annual stage only: 2019 rail length as a share of the 2025 rail. */
  const annualRatio =
    totalCurrent > 0
      ? Math.min(1, Math.max(0, totalPrevious / totalCurrent))
      : 0;

  return (
    <div
      className="market-evidence-frame"
      data-evidence-frame
      data-active-index={displayedActiveIndex}
      data-comparison={comparing ? 'true' : 'false'}
      data-reduced-motion={reducedMotion ? 'true' : 'false'}
    >
      <figure className="market-evidence-field" data-evidence-field>
        <div className="market-evidence-controls" data-evidence-controls>
          {comparisonMode === 'toggle' ? (
            <button
              type="button"
              className="market-evidence-compare-toggle"
              data-evidence-compare-toggle
              aria-pressed={comparing}
              onClick={() => setToggleComparing((open) => !open)}
            >
              Compare 2019
            </button>
          ) : (
            <span className="market-evidence-comparison-label">
              2019 vs 2025
            </span>
          )}
          <p className="market-evidence-key" data-evidence-key>
            {comparing ? (
              <span
                className="market-evidence-key-item market-evidence-key-item--previous"
                data-evidence-key-year={PREVIOUS_YEAR}
              >
                {PREVIOUS_YEAR}
              </span>
            ) : null}
            <span
              className="market-evidence-key-item market-evidence-key-item--current"
              data-evidence-key-year={CURRENT_YEAR}
            >
              {CURRENT_YEAR}
            </span>
          </p>
        </div>

        {comparing && showAnnualComparison ? (
          <div
            className="market-evidence-annual"
            data-evidence-annual
            data-annual-ratio={annualRatio.toFixed(6)}
          >
            <p
              className="market-evidence-annual-anchor market-evidence-annual-anchor--previous"
              data-evidence-total={PREVIOUS_YEAR}
            >
              <span className="market-evidence-annual-year">
                {PREVIOUS_YEAR}
              </span>{' '}
              <strong className="market-evidence-annual-value">
                {formatCount(totalPrevious)}
              </strong>
              <span
                className="market-evidence-annual-note"
                data-evidence-baseline-note
              >
                pre-pandemic
              </span>
            </p>
            <span
              className="market-evidence-annual-track market-evidence-annual-track--previous"
              aria-hidden
              data-evidence-annual-track={PREVIOUS_YEAR}
            >
              <span
                className="market-evidence-annual-baseline"
                data-evidence-annual-baseline
                style={{ width: toPercent(annualRatio) }}
              />
              <span
                className="market-evidence-annual-extension"
                data-evidence-annual-extension
                style={{
                  left: toPercent(annualRatio),
                  width: toPercent(1 - annualRatio),
                }}
              />
            </span>
            <p
              className="market-evidence-annual-anchor market-evidence-annual-anchor--current"
              data-evidence-total={CURRENT_YEAR}
            >
              <span className="market-evidence-annual-year">
                {CURRENT_YEAR}
              </span>{' '}
              <strong className="market-evidence-annual-value">
                {formatCount(totalCurrent)}
              </strong>
            </p>
            <span
              className="market-evidence-annual-track market-evidence-annual-track--current"
              aria-hidden
              data-evidence-annual-track={CURRENT_YEAR}
            >
              <span
                className="market-evidence-annual-fill"
                data-evidence-annual-fill
              />
            </span>
            <p className="market-evidence-annual-delta" data-evidence-delta>
              <strong
                className="market-evidence-annual-delta-percent"
                data-evidence-delta-percent
              >
                {formatPercentChange(totalPrevious, totalCurrent)}
              </strong>
              <span
                className="market-evidence-annual-delta-count"
                data-evidence-delta-count
              >
                {formatDelta(totalCurrent - totalPrevious)} cases
              </span>
            </p>
          </div>
        ) : null}

        {comparing && activeDatum ? (
          <figcaption
            className="sr-only"
            data-evidence-readout
            aria-live="polite"
          >
            {activeDatum.label}: {PREVIOUS_YEAR}{' '}
            <span data-evidence-readout-year={PREVIOUS_YEAR}>
              {formatCount(activeDatum.previousValue)}
            </span>{' '}
            · {CURRENT_YEAR}{' '}
            <span data-evidence-readout-year={CURRENT_YEAR}>
              {formatCount(activeDatum.value)}
            </span>
          </figcaption>
        ) : null}
      </figure>

      <ol className="market-evidence-rows" data-evidence-rows>
        {displayedData.map((datum, index) => (
          <EvidenceRow
            key={datum.label}
            datum={datum}
            index={index}
            active={index === displayedActiveIndex}
            comparing={comparing}
            onActivate={
              onActivateIndex ? () => onActivateIndex(index) : undefined
            }
          />
        ))}
      </ol>
    </div>
  );
}

function EvidenceRow({
  datum,
  index,
  active,
  comparing,
  onActivate,
}: {
  datum: MarketEvidenceDatum;
  index: number;
  active: boolean;
  comparing: boolean;
  onActivate?: () => void;
}) {
  const previousRatio = getMarketEvidenceRatio(datum.previousValue);
  const currentRatio = getMarketEvidenceRatio(datum.value);
  const direction = getDirection(datum.previousValue, datum.value);
  const spanStart = Math.min(previousRatio, currentRatio);
  const spanLength = Math.abs(currentRatio - previousRatio);
  const arrow =
    direction === 'increase'
      ? 'forward'
      : direction === 'decrease'
        ? 'reverse'
        : 'none';

  return (
    <li
      className="market-evidence-row"
      data-evidence-row={index}
      data-active={active ? 'true' : 'false'}
      data-direction={comparing ? direction : undefined}
    >
      <button
        type="button"
        className="market-evidence-row-button"
        data-evidence-row-button={index}
        aria-pressed={active}
        onClick={onActivate}
        onFocus={onActivate}
        onMouseEnter={onActivate}
      >
        <span className="market-evidence-row-label">{datum.label}</span>
        {comparing ? (
          <span
            className="market-evidence-row-value market-evidence-row-value--previous"
            data-evidence-row-year={PREVIOUS_YEAR}
          >
            <span className="sr-only">{PREVIOUS_YEAR} </span>
            {formatCount(datum.previousValue)}
          </span>
        ) : null}
        <span
          className="market-evidence-row-rail"
          aria-hidden
          data-evidence-rail={index}
          data-direction={comparing ? direction : undefined}
          data-line={
            comparing
              ? direction === 'decrease'
                ? 'dashed'
                : 'solid'
              : undefined
          }
          data-arrow={comparing ? arrow : undefined}
          data-ratio-current={currentRatio.toFixed(6)}
          data-ratio-previous={comparing ? previousRatio.toFixed(6) : undefined}
        >
          {comparing ? (
            <>
              <span className="market-evidence-row-track" />
              <span className="market-evidence-row-direction-guide" />
              <span
                className="market-evidence-row-connector"
                style={{
                  left: toPercent(spanStart),
                  width: toPercent(spanLength),
                }}
              />
              <span
                className="market-evidence-row-marker market-evidence-row-marker--start"
                style={{ left: toPercent(previousRatio) }}
              />
              <span
                className="market-evidence-row-marker market-evidence-row-marker--end"
                style={{ left: toPercent(currentRatio) }}
              />
            </>
          ) : (
            <span
              className="market-evidence-row-bar"
              style={{ width: toPercent(currentRatio) }}
            />
          )}
        </span>
        <span
          className="market-evidence-row-value market-evidence-row-value--current"
          data-evidence-row-year={CURRENT_YEAR}
        >
          {comparing ? <span className="sr-only">{CURRENT_YEAR} </span> : null}
          <CountUp
            target={datum.value}
            duration={1.2}
            delay={0.5 + index * 0.08}
            formatter={(value) => formatCount(Math.round(value))}
          />
        </span>
        {comparing ? (
          <span
            className="market-evidence-row-delta"
            data-evidence-row-delta={index}
          >
            <strong
              className="market-evidence-row-percent"
              data-evidence-row-percent={index}
            >
              {formatPercentChange(datum.previousValue, datum.value)}
            </strong>
            <span
              className="market-evidence-row-count"
              data-evidence-row-count={index}
            >
              {formatDelta(datum.value - datum.previousValue)}
            </span>
          </span>
        ) : null}
      </button>
    </li>
  );
}
